import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tag, Tooltip } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import DriverClassificationContext from "../../../context/driver-classification/context";
import { IDriverClassification } from "../../../models/driver-classification/response";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import styles from "./styles.module.scss";

interface IProps {}

const DriverClassificationTable: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(
    DriverClassificationContext
  );

  const columns: ColumnProps<IDriverClassification>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 80,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      sorter: {
        multiple: 2,
      },
      width: 200,
    },
    {
      title: t("description"),
      dataIndex: "description",
      align: "center",
      key: "description",
      width: 250,
      render: (description: string) => (
        <Tooltip title={description}>
          <span>
            {description
              ? description.length > 50
                ? `${description.substring(0, 50)}...`
                : description
              : "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t("calculating_delivery_cost_per_kilometer"),
      dataIndex: "calculatingDeliveryCostPerKilometer",
      align: "center",
      key: "calculatingDeliveryCostPerKilometer",
      sorter: {
        multiple: 3,
      },
      width: 150,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("yes")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("no")}
          </Tag>
        ),
    },
    {
      title: t("delivery_cost_per_kilometer"),
      dataIndex: "deliveryCostPerKilometer",
      align: "center",
      key: "deliveryCostPerKilometer",
      sorter: {
        multiple: 4,
      },
      width: 120,
      render: (value: string) => `${value} ${t("currency")}`,
    },
    {
      title: t("fixed_order_delivery_cost"),
      dataIndex: "orderDeliveryCost",
      align: "center",
      key: "orderDeliveryCost",
      sorter: {
        multiple: 5,
      },
      width: 150,
      render: (value: string) => `${value} ${t("currency")}`,
    },
    {
      title: t("extra_order_fixed_bonus_enabled"),
      dataIndex: "extraOrderFixedBonusEnabled",
      align: "center",
      key: "extraOrderFixedBonusEnabled",
      sorter: {
        multiple: 6,
      },
      width: 150,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("yes")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("no")}
          </Tag>
        ),
    },
    {
      title: t("extra_order_fixed_bonus_amount"),
      dataIndex: "extraOrderFixedBonusAmount",
      align: "center",
      key: "extraOrderFixedBonusAmount",
      sorter: {
        multiple: 7,
      },
      width: 150,
      render: (value: string) => `${value} ${t("currency")}`,
    },
    {
      title: t("actions"),
      width: 200,
      dataIndex: "",
      align: "center",
      key: "actions",
      render: (_: any, record) => (
        <Space>
          <ViewBtn
            onClick={(e) => {
              const url = `/driver-classifications/details/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <EditBtn
            onClick={(e) => {
              const url = `/driver-classifications/update/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <DeleteBtn
            onConfirm={async () => {
              await actions.deleteDriverClassification(record?.id);
            }}
            loading={loading.includes("delete")}
          />
        </Space>
      ),
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(
    query?.filters ?? []
  );

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        className={styles.table}
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default DriverClassificationTable;
